import { APP_BASE_HREF } from '@angular/common';
import { Directive, EventEmitter, Inject, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { BehaviorSubject, lastValueFrom, Subscription, filter } from 'rxjs';

import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';

import { SearchCriteriaSourcesEnum } from '../../../../api/mentions/enum/search-criteria-sources.enum';
import { DuplicatesStatusEnum } from '../../../../Common/Filter/enums/duplicates-status.enum';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { JournodexSourceModel } from '../../../../Common/Journodex/models/journodex-source.model';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { AbstractFormFieldComponent } from '../../../../Common/PageBuilder/components/FormField/abstract-form-field.component';
import { SourceGroup } from '../../../../Common/Sources/models/source-group.model';
import { SourceGroupService } from '../../../../Common/Sources/services/source-group.service';
import { SourceService } from '../../../../Common/Sources/services/source.service';
import { TagGroupModel } from '../../../../Common/Tags/models/tag-group.model';
import { TagModel } from '../../../../Common/Tags/models/tag.model';
import { TagGroupsService } from '../../../../Common/Tags/services/tag-groups.service';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { Theme } from '../../../../Social/Intelligence/models/theme.model';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { SearchRangeModel } from '../../../Core/models/search-range.model';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { FolderService } from '../../../Core/services/folder.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { MonitoringSeriesTypeEnum } from '../../enums/monitoring-series-type.enum';
import { Folder } from '../../models/folder.model';
import { MediaSubscription } from '../../models/media-subscription.model';
import { MentionSearchCriteriaModel } from '../../models/mention-search-criteria.model';
import { MentionSearchListItemModel } from '../../models/mention-search-list-item.model';
import { MentionSearchQueryModel } from '../../models/mention-search-query.model';
import { MentionSearchModel } from '../../models/mention-search.model';
import { MonitoringBackEndDataSourceModel } from '../../models/monitoring-back-end-data-source.model';
import {
    getMonitoringDateQueryInputFolderOptions,
    getMonitoringDateQueryInputTagOptions,
    getMonitoringDateQueryInputTopicOptions
} from '../../models/monitoring-date-query-input-options';
import { MonitoringSourcePickerFieldModel } from '../../models/monitoring-source-picker-field.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { defaultDateTypeOptions, savedSourceDateTypeOptions } from '../../../../Common/Forms/components/DateRangeSelector/date-range-selector.component';
import { RelativeTimezoneDateTypeEnum } from '@api/mentions/enum/relative-timezone-date-type.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MediaTypeModel } from '../../models/media-type.model';
import { BlockModel } from '../../../../Common/PageBuilder/models/block.model';
import { CachedDateService } from '@Common/MonitorShared/services/cached-date.service';
import * as uuid from 'uuid';
import { AgprComponentStyleEnum } from '@Common/Basic/enums/social-analysis-types.enum';
import { AiAgentsListService } from '../../../../AutomationAgents/services/ai-agents-list.service';
import { CurationAgentModel } from '../../../../AutomationAgents/models/curation-agent.model';
import { AgentRouteSectionEnum, agentSectionRoute } from '../../../../AutomationAgents/AutomationAgentsRouter/automation-agents-route.const';

/**
 * This component is designed to work with pagebuilder as well as being used on its own.
 *
 * It will allow the user to select a datasource from the Monitoring Feature module
 * and emit an event with the type of datasource selected along with the id of the relevant datasource.
 *
 * In the event this component is used with pageBuilders "blockform" component this component will
 * emit the event which will be added to display element of the relevant block.
 */
@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class MonitoringSourcePickerFieldComponent extends AbstractFormFieldComponent implements OnInit, OnDestroy, OnChanges {

    /** Field model to initialize some of the values needed for the component to work */
    @Input() declare field: MonitoringSourcePickerFieldModel;
    @Input() id: string;
    /** value of the datasource*/
    @Input() value: MonitoringBackEndDataSourceModel = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.allSavedCoverage);
    // Accessibility inputs
    @Input() ariaInvalid: boolean;
    @Input() ariaDescribedBy: string;
    @Input() onlySocial: boolean = false;
    @Input() showHiddenOption: boolean = false;
    @Input() showErrors: boolean = true;
    @Input() showSortOrder: boolean = false;
    /** AI agents left out of the list - an agent cannot be its own source */
    @Input() excludedAiAgentIds: number[] = [];

    /** Emits and event with the source type and source id */
    @Output() changes: EventEmitter<MonitoringBackEndDataSourceModel> =  new EventEmitter<MonitoringBackEndDataSourceModel>();
    @Output() sourceNotFound: EventEmitter<boolean> =  new EventEmitter<boolean>();

    sourceType: MonitoringSeriesTypeEnum = MonitoringSeriesTypeEnum.allSavedCoverage;
    sourceDropdownOption: DropdownOptionModel;

    numberOfResults: number;
    isLoadingResults: boolean;

    suggestions$ = new BehaviorSubject<DropdownOptionModel[]>([]);

    filteredOptions: DropdownOptionModel[];

    selectedSource: DropdownOptionModel;
    dateTypeOptions: DropdownOptionModel[] = defaultDateTypeOptions;
    timezone: string;
    hideDateRecurring: boolean = false; // will just be used in briefing / analysis block, we want to hide date when recurring
    viewSourceError: string = '';

    searchRange: SearchRangeModel = null;

    mentionBlocksOptions: DropdownOptionModel[];
    selectedBlock: BlockModel;
    mentionBlocksLoading: boolean = true;

    dateFilterOptions: DropdownOptionModel[] = getMonitoringDateQueryInputFolderOptions();

    dateQuery: DateQueryModel;
    cachedDateQuery: DateQueryModel;
    dataSourceBlockErrorMsg: string;

    // resource arrays
    topics: MentionSearchListItemModel[];
    topicOptions: DropdownOptionModel<MentionSearchListItemModel>[] = [];

    mediaSubscriptions: MediaSubscription[];
    mediaSubscriptionOptions: DropdownOptionModel<MediaSubscription>[] = [];

    tags: TagModel[];
    tagOptions: DropdownOptionModel<TagModel>[] = [];

    tagGroups: TagGroupModel[];
    tagGroupOptions: DropdownOptionModel<TagGroupModel>[] = [];

    sourceGroups: SourceGroup[];
    sourceGroupOptions: DropdownOptionModel<SourceGroup>[] = [];

    folders: Folder[];
    folderOptions: DropdownOptionModel<Folder>[] = [];

    socialTopics: MediaSubscription[];
    socialTopicsOptions: DropdownOptionModel<MediaSubscription>[] = [];

    socialThemes: Theme[];
    socialThemesOptions: DropdownOptionModel<Theme>[] = [];

    aiAgents: CurationAgentModel[];
    aiAgentOptions: DropdownOptionModel<CurationAgentModel>[] = [];

    // loading booleans
    topicsLoading: boolean = true;
    subscriptionsLoading: boolean = true;
    tagsLoading: boolean = true;
    tagGroupsLoading: boolean = true;
    sourceGroupsLoading: boolean = true;
    foldersLoading: boolean = true;
    socialTopicsLoading: boolean = true;
    themesLoading: boolean = true;
    aiAgentsLoading: boolean = true;

    protected aiAgentsListService: AiAgentsListService = inject(AiAgentsListService);

    private _pendingSourceId: number | null = null;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly seriesType = MonitoringSeriesTypeEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly unSupportedViewSourceTypes = [
        MonitoringSeriesTypeEnum.allCuratedCoverageInBrief,
        MonitoringSeriesTypeEnum.coverageInSection,
        MonitoringSeriesTypeEnum.coverageSurge,
        MonitoringSeriesTypeEnum.currentSearch
    ];
    readonly ariaLabel = {
        [MonitoringSeriesTypeEnum.folder]: 'Monitoring.sourcePickerField.folderLabel',
        [MonitoringSeriesTypeEnum.topic]: 'Monitoring.sourcePickerField.topicLabel',
        [MonitoringSeriesTypeEnum.tag]: 'Monitoring.sourcePickerField.tagLabel',
        [MonitoringSeriesTypeEnum.tagGroup]: 'Monitoring.sourcePickerField.tagGroupLabel',
        [MonitoringSeriesTypeEnum.subscription]: 'Monitoring.sourcePickerField.subscriptionLabel',
        [MonitoringSeriesTypeEnum.sourceGroup]: 'Monitoring.sourcePickerField.sourceGroupLabel',
        [MonitoringSeriesTypeEnum.socialTopic]: 'Monitoring.sourcePickerField.socialTopicLabel',
        [MonitoringSeriesTypeEnum.theme]: 'Monitoring.sourcePickerField.themeLabel',
        [MonitoringSeriesTypeEnum.aiAgent]: 'Monitoring.sourcePickerField.aiAgentLabel',
    };
    readonly placeholder = {
        [MonitoringSeriesTypeEnum.folder]: 'Monitoring.sourcePickerField.folderPlaceholder',
        [MonitoringSeriesTypeEnum.topic]: 'Monitoring.sourcePickerField.topicPlaceholder',
        [MonitoringSeriesTypeEnum.tag]: 'Monitoring.sourcePickerField.tagPlaceholder',
        [MonitoringSeriesTypeEnum.tagGroup]: 'Monitoring.sourcePickerField.tagGroupPlaceholder',
        [MonitoringSeriesTypeEnum.subscription]: 'Monitoring.sourcePickerField.subscriptionPlaceholder',
        [MonitoringSeriesTypeEnum.sourceGroup]: 'Monitoring.sourcePickerField.sourceGroupPlaceholder',
        [MonitoringSeriesTypeEnum.socialTopic]: 'Monitoring.sourcePickerField.socialTopicPlaceholder',
        [MonitoringSeriesTypeEnum.theme]: 'Monitoring.sourcePickerField.themePlaceholder',
        [MonitoringSeriesTypeEnum.aiAgent]: 'Monitoring.sourcePickerField.aiAgentPlaceholder',
    };
    readonly agprComponentStyleEnum = AgprComponentStyleEnum;

    protected subscriptions: Subscription[] = [];

    constructor(
        public topicsService: TopicsService,
        public monitorConfigService: MonitorConfigService,
        public folderService: FolderService,
        public tagsService: TagsService,
        public tagGroupsService: TagGroupsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public sourceGroupService: SourceGroupService,
        public sourceService: SourceService,
        public themeService: ThemeService,
        public agentService: AgentService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public cachedDateService: CachedDateService
    ) {
        super();
    }

    get disabledViewSourceButton() {
        if (this.sourceType === MonitoringSeriesTypeEnum.allSavedCoverage) {
            return false;
        }

        if (this.unSupportedViewSourceTypes.includes(this.sourceType)) {
            return true;
        }

        const id = this.value?.id;
        return id === null || id === undefined;
    }

    ngOnInit() {
        this.updateSearchCriteria();
        this.setupSubscriptions();
        this.initializeDataSourceOptions();
    }

    setupSubscriptions() {
        this.subscriptions.push(
            this.cachedDateService.getCachedSelectedDate().subscribe(
                cachedDate => {
                    this.cachedDateQuery = cachedDate;
                }
            ),
            this.topicsService.getTopics().subscribe(
                (topics) => {
                    if (topics) {
                        const shouldInitialize = !this.topics;
                        this.topics = topics;
                        this.topicOptions = this.topics.map((topic) => new DropdownOptionModel<MentionSearchListItemModel>(topic.name, topic));

                        this.topicsLoading = false;

                        this.emitSuggestions();

                        if (shouldInitialize) {
                            this.initializeSelectedDataSource();
                        } else if (this.sourceType === MonitoringSeriesTypeEnum.topic && this.selectedSource && this.selectedSource.value) {
                            this.selectedSource = this.topicOptions.find(
                                topicOption => (<MentionSearchModel>topicOption.value).id === (<MentionSearchModel>this.selectedSource.value).id
                            );

                            if (!this.selectedSource) {
                                this.selectType(MonitoringSeriesTypeEnum.topic);
                            }
                        } else if (this._pendingSourceId &&
                            this.topicOptions.some(opt => (<MentionSearchModel>opt.value).id === this._pendingSourceId)) {
                            this.initializeSelectedDataSource();
                        }
                    }
                }
            ),
            this.mediaSubscriptionService.all$.subscribe(
                (mediaSubscriptions) => {
                    const shouldInitialize = !this.mediaSubscriptions;
                    this.mediaSubscriptions = mediaSubscriptions;
                    this.mediaSubscriptionOptions = this.mediaSubscriptions.map((x) => new DropdownOptionModel<MediaSubscription>(x.name, x));

                    this.subscriptionsLoading = false;

                    this.emitSuggestions();

                    if (shouldInitialize) {
                        this.initializeSelectedDataSource();
                    } else if (this.sourceType === MonitoringSeriesTypeEnum.subscription && this.selectedSource && this.selectedSource.value) {
                        this.selectedSource = this.mediaSubscriptionOptions.find(
                            mediaSubscriptionOption => (mediaSubscriptionOption.value).id === (this.selectedSource.value).id
                        );

                        if (!this.selectedSource) {
                            this.selectType(MonitoringSeriesTypeEnum.subscription);
                        }
                    }
                }
            ),
            this.folderService.all$.subscribe(
                (folders) => {
                    const shouldInitialize = (!this.folders || this.folders.length < 1) && folders;

                    this.folders = folders || [];
                    this.folderOptions = this.folders.map((x) => new DropdownOptionModel<Folder>(x.name, x));

                    this.foldersLoading = false;

                    this.emitSuggestions();

                    if (shouldInitialize) {
                        this.initializeSelectedDataSource();
                    } else if (this.sourceType === MonitoringSeriesTypeEnum.folder && this.selectedSource && this.selectedSource.value) {
                        this.selectedSource = this.folderOptions.find(
                            folderOption => (<Folder>folderOption.value).id === (<Folder>this.selectedSource.value).id
                        );

                        if (!this.selectedSource) {
                            this.selectType(MonitoringSeriesTypeEnum.folder);
                        }
                    }
                }
            ),
            this.tagsService.all$.subscribe(
                (tagList) => {
                    if (!tagList) {
                        return;
                    }
                    const shouldInitialize = !this.tags || this.tags.length < 1;
                    this.tags = tagList;
                    this.tagOptions = this.tags.map(tag => new DropdownOptionModel<TagModel>(tag.name, tag));

                    this.tagsLoading = false;

                    this.emitSuggestions();

                    if (shouldInitialize) {
                        this.initializeSelectedDataSource();
                    } else if (this.sourceType === MonitoringSeriesTypeEnum.tag && this.selectedSource && this.selectedSource.value) {
                        this.selectedSource = this.tagOptions.find(
                            tagOption => (<TagModel>tagOption.value).id === (<TagModel>this.selectedSource.value).id
                        );

                        if (!this.selectedSource) {
                            this.selectType(MonitoringSeriesTypeEnum.folder);
                        }
                    }
                }
            ),
            this.tagGroupsService.getTagGroupsList().subscribe(
                (tagGroups) => {
                    if (!tagGroups) {
                        return;
                    }

                    const shouldInitialize = !this.tagGroups || this.tagGroups.length < 1;

                    this.tagGroups = tagGroups;
                    this.tagGroupOptions = this.tagGroups.map(tagGroup => new DropdownOptionModel<TagGroupModel>(tagGroup.name, tagGroup));

                    this.tagGroupsLoading = false;

                    this.emitSuggestions();

                    if (shouldInitialize) {
                        this.initializeSelectedDataSource();
                    } else if (this.sourceType === MonitoringSeriesTypeEnum.tagGroup && this.selectedSource && this.selectedSource.value) {
                        this.selectedSource = this.tagGroupOptions.find(
                            tagGroupOption => (<TagGroupModel>tagGroupOption.value).id === (<TagGroupModel>this.selectedSource.value).id
                        );

                        if (!this.selectedSource) {
                            this.selectType(MonitoringSeriesTypeEnum.folder);
                        }
                    }
                }
            ),
            this.monitorConfigService.getSearchRange().subscribe(
                searchRange => {
                    this.searchRange = searchRange;
                }
            ),
            this.monitorConfigService.hasSocialConfigAccess().subscribe(
                hasSocial => {
                    if (!hasSocial) {
                        this.socialTopicsOptions = [];
                        this.socialThemesOptions = [];
                        this.field.dataSourceOptions = this.field.dataSourceOptions.filter(x => {
                            return x.value !== MonitoringSeriesTypeEnum.theme && x.value !== MonitoringSeriesTypeEnum.socialTopic;
                        });

                        if (!this.field.dataSourceOptions.find(x => x.value === this.value.type)) {
                            this.value = null;
                            this.initializeSelectedDataSource();
                        }

                    }
                }
            ),
            this.sourceGroupService.all$.subscribe(
                (sourceGroups) => {
                    if (!sourceGroups) {
                        return;
                    }

                    const shouldInitialize = !this.sourceGroups || this.sourceGroups.length < 1;

                    this.sourceGroups = sourceGroups;
                    this.sourceGroupOptions = this.sourceGroups.map(x => new DropdownOptionModel<SourceGroup>(x.name, x));

                    this.sourceGroupsLoading = false;

                    if (shouldInitialize) {
                        this.initializeSelectedDataSource();

                    } else if (this.sourceType === MonitoringSeriesTypeEnum.sourceGroup && this.selectedSource && this.selectedSource.value) {
                        this.selectedSource = this.sourceGroupOptions.find(
                            sourceGroupOption => (<SourceGroup>sourceGroupOption.value).id === (<SourceGroup>this.selectedSource.value).id
                        );

                        if (!this.selectedSource) {
                            this.selectType(MonitoringSeriesTypeEnum.folder);
                        }
                    }
                }
            ),
            this.agentService.all$.subscribe(
                (agents) => {
                    if (!agents) {
                        return;
                    }
                    const shouldInitialize = !this.socialTopics || this.socialTopics.length < 1;

                    this.socialTopics = agents;
                    this.socialTopicsOptions = this.socialTopics.map(x => new DropdownOptionModel<MediaSubscription>(x.name, x));

                    this.socialTopicsLoading = false;

                    if (shouldInitialize) {
                        this.initializeSelectedDataSource();
                    } else if (this.sourceType === MonitoringSeriesTypeEnum.sourceGroup && this.selectedSource && this.selectedSource.value) {
                        this.selectedSource = this.socialTopicsOptions.find(
                            socialTopicOption => (<SourceGroup>socialTopicOption.value).id === (<SourceGroup>this.selectedSource.value).id
                        );

                        if (!this.selectedSource) {
                            this.selectType(MonitoringSeriesTypeEnum.socialTopic);
                        }
                    }
                }
            ),
            this.themeService.all$.subscribe(
                (themes) => {
                    if (!themes) {
                        return;
                    }
                    const shouldInitialize = !this.socialThemes || this.socialThemes.length < 1;

                    this.socialThemes = themes;
                    this.socialThemesOptions = this.socialThemes.map(theme => new DropdownOptionModel<Theme>(theme.name, theme));

                    this.themesLoading = false;

                    if (shouldInitialize) {
                        this.initializeSelectedDataSource();
                    } else if (this.sourceType === MonitoringSeriesTypeEnum.theme && this.selectedSource && this.selectedSource.value) {
                        this.selectedSource = this.socialThemesOptions.find(
                            socialThemeOption => (<SourceGroup>socialThemeOption.value).id === (<SourceGroup>this.selectedSource.value).id
                        );

                        if (!this.selectedSource) {
                            this.selectType(MonitoringSeriesTypeEnum.theme);
                        }
                    }
                }
            ),
            this.aiAgentsListService.all$.subscribe(
                (aiAgents) => {
                    const shouldInitialize = !this.aiAgents;

                    this.aiAgents = aiAgents.filter(agent => !this.excludedAiAgentIds?.includes(agent.id));
                    this.aiAgentOptions = this.aiAgents.map(agent => new DropdownOptionModel<CurationAgentModel>(agent.name, agent));

                    this.aiAgentsLoading = false;

                    this.removeUnavailableAiAgentOption();
                    this.emitSuggestions();

                    if (shouldInitialize) {
                        this.initializeSelectedDataSource();
                    }
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.timezone = preferences?.timeZone;
                }
            )
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value')) {
            this.initializeSelectedDataSource();
        }
    }

    public async updateSearchCriteria(sourceType?: MonitoringSeriesTypeEnum): Promise<void> {
        const sourceTypeToUse = sourceType ?? this.sourceType;

        let searchCriteriaModel = new MentionSearchCriteriaModel();
        searchCriteriaModel.isAccountSearch = !(sourceTypeToUse === MonitoringSeriesTypeEnum.topic
                                             || sourceTypeToUse === MonitoringSeriesTypeEnum.subscription
                                             || sourceTypeToUse === MonitoringSeriesTypeEnum.socialTopic
                                             || sourceTypeToUse === MonitoringSeriesTypeEnum.theme);

        if (sourceTypeToUse === MonitoringSeriesTypeEnum.currentSearch) {
            searchCriteriaModel.isAccountSearch = this.value.combinedSearchCriteria?.isAccountSearch;
        }
        if (this.value && this.value.id) {
            switch (sourceTypeToUse) {
                case MonitoringSeriesTypeEnum.allSavedCoverage:
                    break;
                case MonitoringSeriesTypeEnum.topic:
                    try {
                        const topic = await lastValueFrom(this.topicsService.getTopic(this.value.id));
                        searchCriteriaModel = topic.searchCriteria.clone();
                    } catch (err) {
                        searchCriteriaModel = null;
                    }
                    break;
                case MonitoringSeriesTypeEnum.subscription:
                    const mentionSearchModel = new MentionSearchModel();
                    mentionSearchModel.searchCriteria.query.subscription.include = [this.value.id];
                    searchCriteriaModel = mentionSearchModel.searchCriteria;
                    break;
                case MonitoringSeriesTypeEnum.folder:
                    searchCriteriaModel.query.folder.include = [this.value.id];
                    break;
                case MonitoringSeriesTypeEnum.tag:
                    searchCriteriaModel.query.tag.include = [this.value.id];
                    break;
                case MonitoringSeriesTypeEnum.tagGroup:
                    searchCriteriaModel.query.tagGroup.include = [ this.value.id ];
                    break;
                case MonitoringSeriesTypeEnum.aiAgent:
                    searchCriteriaModel.query.aiAgent = { include: [ this.value.id ], exclude: [] };
                    break;
                case MonitoringSeriesTypeEnum.sourceGroup:
                    searchCriteriaModel.query.includedSources  = [ new JournodexSourceModel({
                        id: this.value.id,
                        entityType: SearchCriteriaSourcesEnum.sourceGroups,
                        // In mention search query model, source.entityName is used to convert it to sourceGroup include id for the easy add of chips
                        entityName: this.value.id + ''
                    }) ];

                    break;
                case MonitoringSeriesTypeEnum.socialTopic:
                    searchCriteriaModel.query.agentId = this.value.id;
                    searchCriteriaModel.query.agent.include = [];
                    searchCriteriaModel.query.agent.include.push(this.value.id);
                    break;
                case MonitoringSeriesTypeEnum.theme:
                    searchCriteriaModel.query.themeId = this.value.id;
                    searchCriteriaModel.query.theme.include = [];
                    searchCriteriaModel.query.theme.include.push(this.value.id);
                    break;

            }
        } else if (sourceTypeToUse === MonitoringSeriesTypeEnum.allSavedCoverage) {
            searchCriteriaModel.query.dateQuery = DateQueryModel.getAllQuery();
        } else if (sourceTypeToUse === MonitoringSeriesTypeEnum.currentSearch) {
            searchCriteriaModel.query = this.value.combinedSearchCriteria.query;
        } else {
            searchCriteriaModel = null;
        }

        if (searchCriteriaModel && this.dateQuery) {
            searchCriteriaModel.query.dateQuery = this.dateQuery;
        }

        if (this.onlySocial && searchCriteriaModel) {
            searchCriteriaModel.query.includedMediaTypes = MediaTypeModel.getSocialMediaTypes();
        }

        this.value.sourceSearchCriteria = searchCriteriaModel;
        this.emitChanges();
    }

    emitChanges() {
        this.changes.emit(this.value);
    }

    sourceIsValid(): boolean {
        if ((this.sourceType === MonitoringSeriesTypeEnum.allSavedCoverage ||
            this.sourceType === MonitoringSeriesTypeEnum.currentSearch) ||
            this.value?.id
        ) {
            return true;
        }

        return false;
    }

    /**
     * Selects a type of series
     */
    async selectType(type: MonitoringSeriesTypeEnum): Promise<void> {
        this.sourceType = type;
        this.selectedSource = null;
        this.resetFilters();
        this.value.type = this.sourceType;
        this.value.id = null;

        this.setAccountFilters();
        await this.updateSearchCriteria();
        this.clearSearch();

        this.search();

        this.emitSuggestions();
        this.updateDateFilterOptions();
        this.updateDateTypeOptions();
        this.setSafeTopicDateQuery();
        this.emitChanges();
    }

    /**
     * Selects the series data source
     */
    async selectItem(item: DropdownOptionModel): Promise<void> {
        this.clearSearch();

        if (!item) {
            this.value.id = null;
            this.value.dataSourceName = null;
            this.emitChanges();
            return;
        }

        this.value.id = item.value.id;
        this.value.dataSourceName = item.value.name;
        await this.updateSearchCriteria();

        this.search();

        this.emitChanges();
    }

    /**
     * Resets the source picker to default values
     */
    resetSourcePicker(): void {
        this.clearSearch();
        this.resetFilters();
        this.value = null;
        this.emitChanges();
        this.selectedSource = null;
        this.viewSourceError = '';
        this.initializeSelectedDataSource();
    }


    emitSuggestions(): void {
        switch (this.sourceType) {
            case MonitoringSeriesTypeEnum.topic:
                this.suggestions$.next(this.topicOptions);
                break;
            case MonitoringSeriesTypeEnum.subscription:
                this.suggestions$.next(this.mediaSubscriptionOptions);
                break;
            case MonitoringSeriesTypeEnum.tag:
                this.suggestions$.next(this.tagOptions);
                break;
            case MonitoringSeriesTypeEnum.tagGroup:
                this.suggestions$.next(this.tagGroupOptions);
                break;
            case MonitoringSeriesTypeEnum.folder:
                this.suggestions$.next(this.folderOptions);
                break;
            case MonitoringSeriesTypeEnum.sourceGroup:
                this.suggestions$.next(this.sourceGroupOptions);
                break;
            case MonitoringSeriesTypeEnum.socialTopic:
                this.suggestions$.next(this.socialTopicsOptions);
                break;
            case MonitoringSeriesTypeEnum.theme:
                this.suggestions$.next(this.socialThemesOptions);
                break;
            case MonitoringSeriesTypeEnum.aiAgent:
                this.suggestions$.next(this.aiAgentOptions);
                break;
            case MonitoringSeriesTypeEnum.coverageInSection:
                this.suggestions$.next(this.mentionBlocksOptions);
                break;
        }
    }

    /**
     * Handles change in date range
     * @param dateQuery -  the date to change the selected date to
     */
    async dateRangeChanged(dateQuery: DateQueryModel): Promise<void> {
        this.clearSearch();
        this.setSearchDateQuery(dateQuery);
        if (this.sourceIsValid()) {
            await this.updateSearchCriteria();
            this.search();
        }

        this.emitChanges();
    }

    /**
     * Updates the datefilter options based on the type of datasource selected
     */
    async updateDateFilterOptions(isInitialization: boolean = false): Promise<void> {
        if (this.sourceType === MonitoringSeriesTypeEnum.topic
            || this.sourceType === MonitoringSeriesTypeEnum.subscription
            || this.sourceType === MonitoringSeriesTypeEnum.socialTopic
            || this.sourceType === MonitoringSeriesTypeEnum.theme
            || this.sourceType === MonitoringSeriesTypeEnum.coverageSurge
        ) {
            this.dateFilterOptions = getMonitoringDateQueryInputTopicOptions();
        } else if (this.sourceType === MonitoringSeriesTypeEnum.tag) {
            this.dateFilterOptions = getMonitoringDateQueryInputTagOptions();
        } else {
            this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();
        }

        if (isInitialization && this.dateQuery?.type === DateQueryTypeEnum.ABSOLUTE) {
            return;
        }

        // backwards compatibility layer for old relative time
        // if date query is 'last 24 hours', 'last 3 days', 'last 7 days', 'last 30 days', convert it to the correct relative timezone query
        if (
            this.dateQuery?.type === DateQueryTypeEnum.RELATIVE &&
            this.dateQuery?.relativeStartOffsetH === -24
            || this.dateQuery?.relativeStartOffsetH === -72
            ||  this.dateQuery?.relativeStartOffsetH === -168
            ||  this.dateQuery?.relativeStartOffsetH === -720

        ) {
            let relativeTimezoneQuery: DateQueryModel = new DateQueryModel();
            relativeTimezoneQuery.timezone = this.timezone;
            switch (this.dateQuery.relativeStartOffsetH) {
                case -24:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_24_HOURS);
                    break;
                case -72:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_3_DAYS);
                    break;
                case -168:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_7_DAYS);
                    break;
                case -720:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_30_DAYS);
                    break;
            }
            relativeTimezoneQuery.dateType = this.dateQuery.dateType;
            this.dateQuery = relativeTimezoneQuery;
        }

        const optionInList = this.dateFilterOptions.find((option) => {
            return DateQueryModel.equals(option.value.query, this.dateQuery);
        });

        // We changed the options in the list, if the option chosen is no longer available, choose the first option in the list
        if (!optionInList) {
            if (this.cachedDateQuery) {
                const initialVal = this.dateFilterOptions.find(x => {
                    return DateQueryModel.equals(x.value.query, this.cachedDateQuery, false);
                });
                const hasCustom = this.dateFilterOptions.find(x => {
                    return x.value.query.type === DateQueryTypeEnum.ABSOLUTE;
                });
                if (initialVal) {
                    this.dateQuery = initialVal.value.query;
                } else if (hasCustom && (this.cachedDateQuery.type === DateQueryTypeEnum.ABSOLUTE)) {
                    // absolute date range will not be part of the dateFilterOptions
                    this.dateQuery = this.cachedDateQuery;
                } else {
                    this.dateQuery = this.dateFilterOptions[0].value.query;
                }
            } else {
                this.dateQuery = this.dateFilterOptions[0].value.query;
            }

            this.dateQuery.timezone = this.timezone;
            await this.dateRangeChanged(this.dateQuery);
        }
    }

    /**
     * Initialize the datasource options the user can select from
     */
    initializeDataSourceOptions(): void {
        this.removeUnavailableAiAgentOption();
        this.field.dataSourceOptions.forEach(source => source.label = this.localizationService.transform(source.label));
        const option = this.field.dataSourceOptions.find(opt => opt.value === this.sourceType);
        if (!option) {
            this.sourceType = this.field.dataSourceOptions[0].value;
            this.sourceDropdownOption = this.field.dataSourceOptions[0];
        } else {
            this.sourceDropdownOption = option;
        }

        this.setAccountFilters();
        this.emitSuggestions();
        this.updateDateTypeOptions();
    }

    /**
     * Accounts without the agentic feature have no AI agents to pick from, so the type is dropped. Run
     * whenever the options are set up, since a host can hand the field a fresh list at any time.
     */
    private removeUnavailableAiAgentOption(): void {
        if (this.aiAgents && !this.aiAgents.length) {
            this.field.dataSourceOptions = this.field.dataSourceOptions.filter(x => x.value !== MonitoringSeriesTypeEnum.aiAgent);
        }
    }

    /**
     * Initializes the component to select the appropriate datasource type & id if available
     */
    async initializeSelectedDataSource(): Promise<void> {
        if (!(this.folderOptions &&
            this.topicOptions &&
            this.tagOptions &&
            this.tagGroupOptions &&
            this.mediaSubscriptionOptions &&
            this.sourceGroupOptions &&
            this.socialTopicsOptions &&
            this.socialThemesOptions &&
            this.aiAgentOptions)
        ) {
            return;
        }

        if (!this.value) {
            // component is loading without a data source pre selected
            await this.initializeEmptyDataSource();

            return;
        }

        this.sourceType = this.value.type;
        this.initializeDataSourceOptions();

        if (!this.value.id) {
            this.clearSearch();
        }

        let sourceLoading: boolean = false;
        switch (this.value.type) {
            case MonitoringSeriesTypeEnum.folder:
                this.selectedSource = this.folderOptions.find((folderOption) => {
                    return folderOption.value.id === this.value.id;
                });
                sourceLoading = this.foldersLoading;
                break;
            case MonitoringSeriesTypeEnum.topic:
                this.selectedSource = this.topicOptions.find((topicOption) => {
                    return topicOption.value.id === this.value.id;
                });
                sourceLoading = this.topicsLoading;
                break;
            case MonitoringSeriesTypeEnum.subscription:
                this.selectedSource = this.mediaSubscriptionOptions.find((mediaSubscriptionOption) => {
                    return mediaSubscriptionOption.value.id === this.value.id;
                });
                sourceLoading = this.subscriptionsLoading;
                break;
            case MonitoringSeriesTypeEnum.tag:
                this.selectedSource = this.tagOptions.find((tagOption) => {
                    return tagOption.value.id === this.value.id;
                });
                sourceLoading = this.tagsLoading;
                break;
            case MonitoringSeriesTypeEnum.tagGroup:
                this.selectedSource = this.tagGroupOptions.find(tagGroupOption => {
                    return tagGroupOption.value.id === this.value.id;
                });
                sourceLoading = this.tagGroupsLoading;
                break;
            case MonitoringSeriesTypeEnum.sourceGroup:
                this.selectedSource = this.sourceGroupOptions.find(tagGroupOption => {
                    return tagGroupOption.value.id === this.value.id;
                });
                sourceLoading = this.sourceGroupsLoading;
                break;
            case MonitoringSeriesTypeEnum.socialTopic:
                this.selectedSource = this.socialTopicsOptions.find(tagGroupOption => {
                    return tagGroupOption.value.id === this.value.id;
                });
                sourceLoading = this.socialTopicsLoading;
                break;
            case MonitoringSeriesTypeEnum.theme:
                this.selectedSource = this.socialThemesOptions.find(tagGroupOption => {
                    return tagGroupOption.value.id === this.value.id;
                });
                sourceLoading = this.themesLoading;
                break;
            case MonitoringSeriesTypeEnum.aiAgent:
                this.selectedSource = this.aiAgentOptions.find(aiAgentOption => {
                    return aiAgentOption.value.id === this.value.id;
                });
                sourceLoading = this.aiAgentsLoading;
                break;
        }

        if (this.value.id && !this.selectedSource && !sourceLoading) {
            this._pendingSourceId = this.value.type === MonitoringSeriesTypeEnum.topic ? this.value.id : null;
            await this.initializeEmptyDataSource();
            this.sourceNotFound.emit(true);
            return;
        } else {
            this._pendingSourceId = null;
            this.sourceNotFound.emit(false);
        }

        const savedDateQuery = this.value.filterSearchCriteria?.query?.dateQuery
            ? this.value.filterSearchCriteria.query.dateQuery as DateQueryModel
            : null;

        const hasParentDateQuery = !!this.field?.filterCriteria?.query?.dateQuery;

        if (savedDateQuery && !hasParentDateQuery) {
            this.dateQuery = savedDateQuery;
            this.setSearchDateQuery(this.dateQuery);
        }

        this.updateDateFilterOptions(true);

        let filterCriteria: MentionSearchCriteriaModel;
        let filterQuery: MentionSearchQueryModel;
        if (this.value.filterSearchCriteria) {
            filterCriteria = this.value.filterSearchCriteria;
            if (!this.value.filterSearchCriteria.query.isEmpty()) {
                filterQuery = this.value.filterSearchCriteria.query;
            }
        }
        if (this.field.filterCriteria) {
            filterCriteria = this.field.filterCriteria;
            filterQuery = this.field.filterCriteria.query;
        }

        if (filterQuery) {
            this.populateFiltersFromSearchQuery(filterQuery);
        }

        if (filterCriteria) {
            this.setDuplicateFilter(
                filterCriteria.showSimilar ? DuplicatesStatusEnum.show : DuplicatesStatusEnum.hide
            );

            if (filterCriteria.savedFilterIds?.length) {
                this.setSavedFiltersFromIds(filterCriteria.savedFilterIds);
            }

        }

        this.setSafeTopicDateQuery();
        await this.updateSearchCriteria();
        // Re-apply saved dateQuery: async subscriptions may overwrite this.dateQuery during the await
        if (savedDateQuery && !hasParentDateQuery && !DateQueryModel.equals(this.dateQuery, savedDateQuery)) {
            this.dateQuery = savedDateQuery;
            this.setSearchDateQuery(this.dateQuery);
        }
        this.search();
    }

    async initializeEmptyDataSource(): Promise<void> {
        this.sourceType = MonitoringSeriesTypeEnum.folder;
        this.initializeDataSourceOptions();

        this.value = new MonitoringBackEndDataSourceModel(this.sourceType);
        this.selectedSource = null;
        this.updateDateFilterOptions(true);
        await this.updateSearchCriteria();
        if (!this.dateQuery) {
            this.setSearchDateQuery(this.dateFilterOptions[0].value.query);
        }

        this.search();
        this.emitSuggestions();
    }

    /**
     * Sets the number of results to display as feedback
     * @param numberOfResults - number of mentions available
     */
    setNumberOfResults(numberOfResults: number): void {
        this.numberOfResults = numberOfResults;
        if (this.value.isSavedSource()) {
            this.value.curatedMentionCount = numberOfResults;
        } else {
            this.value.dynamicMentionCount = numberOfResults;
        }

        if (!this.field.showTooManyMentionsError && this.numberOfResults > this.field.maxNumberOfMentions) {
            this.numberOfResults = this.field.maxNumberOfMentions;
        }
    }

    /**
     * Safeguards the topics against using invalid date queries
     */
    setSafeTopicDateQuery(): boolean {
        if (!this.searchRange || !(this.sourceType === MonitoringSeriesTypeEnum.subscription || this.sourceType === MonitoringSeriesTypeEnum.topic)) {
            return false;
        }

        if (!this.dateQuery || DateQueryModel.equals(this.dateQuery, DateQueryModel.getAllQuery())) {
            this.dateRangeChanged(DateQueryModel.getLast24HoursQuery());
            return true;
        }

        let changed = false;

        if (this.dateQuery.absoluteRange) {
            if (this.dateQuery.absoluteRange.end < this.searchRange.from) {
                this.dateQuery.absoluteRange = null;
                this.dateQuery.absoluteDate = this.searchRange.from;
                changed = true;
            } else if (this.dateQuery.absoluteRange.start < this.searchRange.from) {
                this.dateQuery.absoluteRange.start = this.searchRange.from;
                changed = true;
            }
        }

        if (this.dateQuery.absoluteDate && this.dateQuery.absoluteDate < this.searchRange.from) {
            this.dateQuery.absoluteDate = this.searchRange.from;
            changed = true;
        }

        if (changed) {
            this.setSearchDateQuery(this.dateQuery);
        }

        return changed;
    }

    /**
     * Handles the onclick for the 'view source' link. Opens relevant source mention results in a new tab.
     */
    viewSource($event: Event): void {

        const sourceId = this.value.id;
        let url = '';
        let errorMessage = '';
        let searchFilters: MentionSearchCriteriaModel;

        if (this.value?.filterSearchCriteria) {
            searchFilters = this.value?.filterSearchCriteria;
        } else {
            searchFilters = new MentionSearchCriteriaModel();
            searchFilters.query.dateQuery = DateQueryModel.clone(this.dateQuery);
        }

        switch (this.value.type) {
            case MonitoringSeriesTypeEnum.topic:
                url = this.appBaseHref + 'Monitoring/topics/topic/' + sourceId;
                break;
            case MonitoringSeriesTypeEnum.subscription:
                url = this.appBaseHref + 'Monitoring/subscriptions/subscription/' + sourceId;
                break;
            case MonitoringSeriesTypeEnum.folder:
                url = this.appBaseHref + 'Monitoring/mentions/folder/' + sourceId;
                break;
            case MonitoringSeriesTypeEnum.tag:
                url = 'Monitoring/mentions/saved';
                searchFilters.query.tag.include = this.addToFilter(searchFilters.query.tag.include, sourceId);
                break;
            case MonitoringSeriesTypeEnum.tagGroup:
                url = 'Monitoring/mentions/saved';
                searchFilters.query.tagGroup.include = this.addToFilter(searchFilters.query.tagGroup.include, sourceId);
                break;
            case MonitoringSeriesTypeEnum.sourceGroup:
                url = 'Monitoring/mentions/saved';
                searchFilters.query.sourceGroup.include = this.addToFilter(searchFilters.query.sourceGroup.include, sourceId);
                break;
            case MonitoringSeriesTypeEnum.theme:
                url = this.appBaseHref + 'Social/themes/theme/' + sourceId + '/postFeed';
                searchFilters.query.theme.include = this.addToFilter(searchFilters.query.theme.include, sourceId);
                break;
            case MonitoringSeriesTypeEnum.allSavedCoverage:
                url = this.appBaseHref + 'Monitoring/mentions/saved';
                break;
            case MonitoringSeriesTypeEnum.aiAgent:
                // The agent's own feed carries its scope, so only the picker's filters travel with it
                url = this.appBaseHref + agentSectionRoute(sourceId, AgentRouteSectionEnum.mentions).substring(1);
                break;
            case MonitoringSeriesTypeEnum.socialTopic:
                const source = this.selectedSource.value;
                const firstTheme = source instanceof MediaSubscription && source.themeIds?.[0];
                if (firstTheme) {
                    url = this.appBaseHref + 'Social/themes/theme/' + firstTheme + '/postFeed';
                    searchFilters.query.agent.include = this.addToFilter(searchFilters.query.agent.include, sourceId);
                } else {
                    errorMessage = 'Monitoring.sourcePickerField.viewSource.errorMessage.socialTopic';
                }
                break;
            default:
                // unknown/undefined source type
                break;
        }

        if (url && searchFilters) {
            const seriesUniqueUuid = uuid.v4();
            url += `?filters=${MentionSearchCriteriaModel.toFilterQueryString(searchFilters, seriesUniqueUuid)}`;
        }

        if (url) {
            window.open(url);
        } else {
            this.viewSourceError = errorMessage;
        }

        ($event?.currentTarget as HTMLElement)?.blur();
    }

    private addToFilter(existingList: number[], item: number) {
        try {
            if (!existingList.includes(item)) {
                return [...existingList, item];
            }
            return existingList;

        } catch {
            return [item];
        }
    }

    updateDateTypeOptions(): void {
        switch (this.sourceType) {
            case MonitoringSeriesTypeEnum.allSavedCoverage:
            case MonitoringSeriesTypeEnum.folder:
            case MonitoringSeriesTypeEnum.tag:
            case MonitoringSeriesTypeEnum.tagGroup:
            case MonitoringSeriesTypeEnum.aiAgent:
                this.dateTypeOptions = savedSourceDateTypeOptions;
                break;
            default:
                this.dateTypeOptions = defaultDateTypeOptions;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.clearSearch();
    }

    abstract resetFilters(): void;
    abstract clearSearch(): void;
    abstract search(): void;
    abstract setSearchDateQuery(dateQuery: DateQueryModel): void;
    abstract setAccountFilters(): void;
    abstract populateFiltersFromSearchQuery(filterQuery: MentionSearchQueryModel): void;
    abstract setDuplicateFilter(duplicatesStatus: DuplicatesStatusEnum): void;
    abstract setSavedFiltersFromIds(ids: number[]): void;
}
